/**
 * Voice Assignment View
 *
 * Story 3.16: Redesigned for locale-aware voice groups.
 * Each episode can have multiple voice groups (default + locale-specific).
 * Voice assignments are managed per group with tabs/cards UI.
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useEpisode,
  useVoiceGroups,
  useCreateVoiceGroup,
  useUpdateVoiceGroup,
  useDeleteVoiceGroup,
  type Persona,
  type VoiceGroupResponse,
} from '../services/episodesApi';
import { useVoices } from '../services/voiceApi';
import { useSuggestVoices, type VoiceAssignment } from '../services/voiceAssignmentApi';
import { useLocalesWithVoice } from '../../dictionaries/services/localesApi';

// ─── Reusable persona voice card ────────────────────────────────────────────
function PersonaVoiceCard({
  persona,
  index,
  isNarrator,
  narratorVoiceInfo,
  selectedVoiceId,
  voices,
  isDuplicate,
  aiReasoning,
  playingVoiceId,
  onVoiceChange,
  onTogglePlay,
}: {
  persona: Persona;
  index: number;
  isNarrator: boolean;
  narratorVoiceInfo: { id: string; name: string } | null;
  selectedVoiceId: string;
  voices: {
    id: number;
    name: string;
    gender: string;
    languageCodes: string[];
    sampleAudioUrl: string | null;
  }[];
  isDuplicate: boolean;
  aiReasoning?: string;
  playingVoiceId: string | null;
  onVoiceChange: (index: number, voiceId: string) => void;
  onTogglePlay: (voiceId: string, sampleUrl: string | null) => void;
}) {
  const selectedVoice = voices.find((v) => String(v.id) === selectedVoiceId);

  return (
    <div
      className={`border rounded-lg p-4 ${
        isNarrator ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
      }`}
      data-testid={`voice-assignment-${index}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900">
          <span className="text-gray-400 text-sm mr-1.5">{index + 1}.</span>
          {persona.name}
          {isNarrator && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded font-normal">
              Narrator
            </span>
          )}
        </h4>
        {!isNarrator && selectedVoiceId && selectedVoice?.sampleAudioUrl && (
          <button
            type="button"
            onClick={() => onTogglePlay(selectedVoiceId, selectedVoice.sampleAudioUrl)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            title={playingVoiceId === selectedVoiceId ? 'Stop' : 'Preview'}
            data-testid={`preview-voice-${index}`}
          >
            {playingVoiceId === selectedVoiceId ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        )}
      </div>

      {persona.description && (
        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{persona.description}</p>
      )}

      {isNarrator && narratorVoiceInfo ? (
        <div className="px-3 py-2 border border-green-300 rounded-md bg-green-100 text-green-800 text-sm">
          {narratorVoiceInfo.name}
          <span className="ml-2 text-xs text-green-600">(from language settings)</span>
        </div>
      ) : (
        <select
          value={selectedVoiceId}
          onChange={(e) => onVoiceChange(index, e.target.value)}
          className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${
            isDuplicate
              ? 'border-yellow-400 focus:ring-yellow-400 bg-yellow-50'
              : selectedVoiceId
                ? 'border-gray-300 focus:ring-blue-500'
                : 'border-gray-300 focus:ring-blue-500 text-gray-400'
          }`}
          data-testid={`voice-select-${index}`}
        >
          <option value="">Select a voice...</option>
          {voices.map((voice) => (
            <option key={voice.id} value={voice.id}>
              {voice.name} ({voice.gender})
            </option>
          ))}
        </select>
      )}

      {aiReasoning && (
        <div className="mt-2 p-2 bg-purple-50 border border-purple-100 rounded text-sm">
          <span className="font-medium text-purple-800">AI: </span>
          <span className="text-purple-700">{aiReasoning}</span>
        </div>
      )}

      {isDuplicate && (
        <p className="text-yellow-600 text-xs mt-2" data-testid={`duplicate-warning-${index}`}>
          Same voice used for multiple personas
        </p>
      )}
    </div>
  );
}

// ─── Main view ──────────────────────────────────────────────────────────────
export function VoiceAssignmentView() {
  const navigate = useNavigate();
  const { episodeId } = useParams<{ episodeId: string }>();

  // ── Existing group state ────────────────────────────────────────────────
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [voiceIds, setVoiceIds] = useState<string[]>([]);
  const [aiReasonings, setAiReasonings] = useState<Record<number, string>>({});
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  // ── New group creation state (inline panel) ─────────────────────────────
  const [isCreating, setIsCreating] = useState(false);
  const [newLocaleCode, setNewLocaleCode] = useState('');
  const [newVoiceIds, setNewVoiceIds] = useState<string[]>([]);
  const [newAiReasonings, setNewAiReasonings] = useState<Record<number, string>>({});

  // ── Data queries ────────────────────────────────────────────────────────
  const { data: episode, isLoading: isLoadingEpisode } = useEpisode(episodeId);
  const { data: voiceGroups = [], isLoading: isLoadingGroups } = useVoiceGroups(episodeId);
  const { data: localesWithVoice, isLoading: isLoadingLocales } = useLocalesWithVoice();
  const { data: allVoices = [], isLoading: isLoadingVoices } = useVoices({ isActive: true });

  const createGroupMutation = useCreateVoiceGroup();
  const updateGroupMutation = useUpdateVoiceGroup();
  const deleteGroupMutation = useDeleteVoiceGroup();

  // Active voice group
  const activeGroup = useMemo(
    () => voiceGroups.find((g) => g.id === activeGroupId) ?? null,
    [voiceGroups, activeGroupId]
  );

  // Select first group when data loads
  useEffect(() => {
    if (voiceGroups.length > 0 && !activeGroupId) {
      setActiveGroupId(voiceGroups[0].id);
    }
  }, [voiceGroups, activeGroupId]);

  // Narrator detection
  const hasNarrator = useMemo(() => {
    if (!episode?.personas?.length) return false;
    return episode.personas[0].role.toLowerCase() === 'narrator';
  }, [episode]);

  // Narrator voice from locale config
  const narratorVoiceInfo = useMemo(() => {
    if (!hasNarrator || !episode?.languageCode || !localesWithVoice) return null;
    if (activeGroup?.localeCode) {
      const locale = localesWithVoice.find((l) => l.localeCode === activeGroup.localeCode);
      if (locale?.defaultVoice) {
        return { id: String(locale.defaultVoice.id), name: locale.defaultVoice.name };
      }
    }
    const locale = localesWithVoice.find(
      (l) => l.languageCode === episode.languageCode && l.order === 1
    );
    if (!locale?.defaultVoice) return null;
    return { id: String(locale.defaultVoice.id), name: locale.defaultVoice.name };
  }, [hasNarrator, episode?.languageCode, localesWithVoice, activeGroup]);

  // Narrator voice for the NEW group panel (may differ by selected locale)
  const newGroupNarratorVoiceInfo = useMemo(() => {
    if (!hasNarrator || !episode?.languageCode || !localesWithVoice) return null;
    if (newLocaleCode) {
      const locale = localesWithVoice.find((l) => l.localeCode === newLocaleCode);
      if (locale?.defaultVoice) {
        return { id: String(locale.defaultVoice.id), name: locale.defaultVoice.name };
      }
    }
    const locale = localesWithVoice.find(
      (l) => l.languageCode === episode.languageCode && l.order === 1
    );
    if (!locale?.defaultVoice) return null;
    return { id: String(locale.defaultVoice.id), name: locale.defaultVoice.name };
  }, [hasNarrator, episode?.languageCode, localesWithVoice, newLocaleCode]);

  const narratorVoiceMissing = hasNarrator && !narratorVoiceInfo;

  // Filter voices by episode language (exclude narrator voice)
  const voices = useMemo(() => {
    if (!episode?.languageCode || !allVoices.length) return [];
    return allVoices.filter((voice) => {
      if (!voice.languageCodes.includes(episode.languageCode)) return false;
      if (narratorVoiceInfo && String(voice.id) === narratorVoiceInfo.id) return false;
      return true;
    });
  }, [allVoices, episode?.languageCode, narratorVoiceInfo]);

  // Available locales for new groups
  const availableLocales = useMemo(() => {
    if (!episode?.languageCode || !localesWithVoice) return [];
    const usedLocales = new Set(voiceGroups.map((g) => g.localeCode).filter(Boolean));
    return localesWithVoice.filter(
      (l) => l.languageCode === episode.languageCode && !usedLocales.has(l.localeCode)
    );
  }, [episode?.languageCode, localesWithVoice, voiceGroups]);

  const hasDefaultGroup = voiceGroups.some((g) => g.localeCode === null);

  // AI voice suggestions (existing group)
  const { refetch: refetchSuggestions, isFetching: suggestionsLoading } = useSuggestVoices(
    episode?.languageCode ? episodeId : undefined,
    episode?.languageCode || '',
    activeGroup?.localeCode
  );

  // AI voice suggestions (new group creation)
  const { refetch: refetchNewSuggestions, isFetching: newSuggestionsLoading } = useSuggestVoices(
    episode?.languageCode && isCreating ? episodeId : undefined,
    episode?.languageCode || '',
    newLocaleCode || null
  );

  // Initialize voiceIds when active group changes
  useEffect(() => {
    if (activeGroup && episode) {
      const initialVoiceIds = activeGroup.voiceIds.map(String);
      if (hasNarrator && narratorVoiceInfo) {
        initialVoiceIds[0] = narratorVoiceInfo.id;
      }
      setVoiceIds(initialVoiceIds);
      setAiReasonings({});
    } else if (episode && !activeGroup) {
      const initialVoiceIds = Array(episode.personas.length).fill('');
      if (hasNarrator && narratorVoiceInfo) {
        initialVoiceIds[0] = narratorVoiceInfo.id;
      }
      setVoiceIds(initialVoiceIds);
    }
  }, [activeGroup, episode, hasNarrator, narratorVoiceInfo]);

  // Cleanup audio
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  // ── Handlers (existing group) ──────────────────────────────────────────

  const handleVoiceChange = (personaIndex: number, voiceId: string) => {
    const updated = [...voiceIds];
    updated[personaIndex] = voiceId;
    setVoiceIds(updated);
    if (aiReasonings[personaIndex]) {
      const r = { ...aiReasonings };
      delete r[personaIndex];
      setAiReasonings(r);
    }
  };

  const handleSuggestVoices = async () => {
    if (!episode?.languageCode || !episodeId) return;
    try {
      const result = await refetchSuggestions();
      if (result.data) {
        const updated = [...voiceIds];
        const reasonings: Record<number, string> = {};
        result.data.suggestedVoiceAssignments.forEach((a: VoiceAssignment) => {
          updated[a.personaIndex] = a.voiceId;
          reasonings[a.personaIndex] = a.reasoning;
        });
        setVoiceIds(updated);
        setAiReasonings(reasonings);
        toast.success('AI voice suggestions applied!');
      }
    } catch {
      toast.error('Failed to get AI voice suggestions.');
    }
  };

  const handleTogglePlay = (voiceId: string, sampleUrl: string | null) => {
    if (!sampleUrl) {
      toast.error('No voice sample available');
      return;
    }
    if (playingVoiceId === voiceId && audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setPlayingVoiceId(null);
      setAudioElement(null);
      return;
    }
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    const audio = new Audio(sampleUrl);
    setAudioElement(audio);
    setPlayingVoiceId(voiceId);
    audio.onended = () => {
      setPlayingVoiceId(null);
      setAudioElement(null);
    };
    audio.onerror = () => {
      toast.error('Failed to play voice sample');
      setPlayingVoiceId(null);
      setAudioElement(null);
    };
    audio.play().catch(() => {
      toast.error('Failed to play voice sample');
      setPlayingVoiceId(null);
      setAudioElement(null);
    });
  };

  const handleSaveGroup = async () => {
    if (!episodeId || !episode || !activeGroup) return;
    if (voiceIds.some((id) => !id)) {
      toast.error('Please assign a voice to each persona');
      return;
    }
    if (voiceIds.length !== episode.personas.length) {
      toast.error(
        `Voice count (${voiceIds.length}) must match persona count (${episode.personas.length})`
      );
      return;
    }
    try {
      await updateGroupMutation.mutateAsync({
        episodeId,
        groupId: activeGroup.id,
        voiceIds: voiceIds.map(Number),
      });
      toast.success('Voice group saved!');
    } catch {
      toast.error('Failed to save voice group.');
    }
  };

  const handleDeleteGroup = async (group: VoiceGroupResponse) => {
    if (!episodeId) return;
    const label = group.localeName || group.localeCode || 'Default';
    if (!window.confirm(`Delete voice group "${label}"?`)) return;
    try {
      await deleteGroupMutation.mutateAsync({ episodeId, groupId: group.id });
      if (activeGroupId === group.id) {
        setActiveGroupId(voiceGroups.find((g) => g.id !== group.id)?.id ?? null);
      }
      toast.success(`Voice group "${label}" deleted`);
    } catch {
      toast.error('Failed to delete voice group.');
    }
  };

  // ── Handlers (new group inline creation) ───────────────────────────────

  const handleStartCreate = () => {
    if (!episode) return;
    // Deselect any active group so the panel has visual focus
    setActiveGroupId(null);
    setIsCreating(true);
    setNewLocaleCode(hasDefaultGroup ? (availableLocales[0]?.localeCode ?? '') : '');
    // Initialize voice slots — narrator auto-filled, rest empty
    const ids = Array(episode.personas.length).fill('');
    if (hasNarrator && newGroupNarratorVoiceInfo) {
      ids[0] = newGroupNarratorVoiceInfo.id;
    }
    setNewVoiceIds(ids);
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewLocaleCode('');
    setNewVoiceIds([]);
    setNewAiReasonings({});
    // Re-select first group if any
    if (voiceGroups.length > 0) setActiveGroupId(voiceGroups[0].id);
  };

  const handleNewVoiceChange = (personaIndex: number, voiceId: string) => {
    const updated = [...newVoiceIds];
    updated[personaIndex] = voiceId;
    setNewVoiceIds(updated);
    if (newAiReasonings[personaIndex]) {
      const r = { ...newAiReasonings };
      delete r[personaIndex];
      setNewAiReasonings(r);
    }
  };

  const handleNewSuggestVoices = async () => {
    if (!episode?.languageCode || !episodeId) return;
    try {
      const result = await refetchNewSuggestions();
      if (result.data) {
        const updated = [...newVoiceIds];
        const reasonings: Record<number, string> = {};
        result.data.suggestedVoiceAssignments.forEach((a: VoiceAssignment) => {
          updated[a.personaIndex] = a.voiceId;
          reasonings[a.personaIndex] = a.reasoning;
        });
        setNewVoiceIds(updated);
        setNewAiReasonings(reasonings);
        toast.success('AI voice suggestions applied!');
      }
    } catch {
      toast.error('Failed to get AI voice suggestions.');
    }
  };

  // Update narrator voice when locale changes in creation panel
  useEffect(() => {
    if (!isCreating || !episode) return;
    if (hasNarrator && newGroupNarratorVoiceInfo) {
      setNewVoiceIds((prev) => {
        const updated = [...prev];
        updated[0] = newGroupNarratorVoiceInfo.id;
        return updated;
      });
    }
  }, [isCreating, newLocaleCode, newGroupNarratorVoiceInfo, hasNarrator, episode]);

  const handleCreateGroup = async () => {
    if (!episodeId || !episode) return;
    const emptySlots = newVoiceIds.filter(
      (id, i) => !id && !(i === 0 && hasNarrator && newGroupNarratorVoiceInfo)
    );
    if (emptySlots.length > 0) {
      toast.error('Please assign a voice to each persona');
      return;
    }
    try {
      const group = await createGroupMutation.mutateAsync({
        episodeId,
        localeCode: newLocaleCode || null,
        voiceIds: newVoiceIds.map(Number),
      });
      setIsCreating(false);
      setNewLocaleCode('');
      setNewVoiceIds([]);
      setNewAiReasonings({});
      setActiveGroupId(group.id);
      toast.success('Voice group created!');
    } catch {
      toast.error('Failed to create voice group.');
    }
  };

  // Duplicate voice detection (for active group)
  const getDuplicateVoices = (ids: string[]) => {
    const counts: Record<string, number> = {};
    ids.forEach((id) => {
      if (id) counts[id] = (counts[id] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, c]) => c > 1)
      .map(([id]) => id);
  };
  const duplicateVoices = getDuplicateVoices(voiceIds);
  const newDuplicateVoices = getDuplicateVoices(newVoiceIds);

  // ── Loading / error states ─────────────────────────────────────────────

  if (isLoadingEpisode || isLoadingVoices || isLoadingLocales || isLoadingGroups) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Episode not found
        </div>
        <Link to="/episodes" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          &larr; Back to Episodes
        </Link>
      </div>
    );
  }

  if (narratorVoiceMissing) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Narrator Voice Not Configured</h2>
          <p className="text-red-700 mb-4">
            This episode has a narrator persona, but no default voice is configured for language{' '}
            <strong>{episode.languageCode.toUpperCase()}</strong>.
          </p>
          <p className="text-red-600 text-sm">
            Configure a default voice in{' '}
            <Link
              to="/dictionaries/locales"
              className="text-red-800 underline hover:text-red-900 font-medium"
            >
              Dictionaries &rarr; Locales
            </Link>
            .
          </p>
        </div>
        <Link to="/episodes" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          &larr; Back to Episodes
        </Link>
      </div>
    );
  }

  // Can add more groups?
  const canAddGroup = !isCreating && (availableLocales.length > 0 || !hasDefaultGroup);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link
            to="/episodes"
            className="text-sm text-gray-500 hover:text-gray-700 mb-1 inline-block"
          >
            &larr; Episodes
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Voice Groups</h1>
          <p className="text-gray-500 mt-1 text-sm">
            {episode.name}
            <span className="mx-2 text-gray-300">|</span>
            <span className="uppercase tracking-wide text-xs font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              {episode.languageCode}
            </span>
            <span className="mx-2 text-gray-300">|</span>
            {episode.personas.length} persona{episode.personas.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Voice Group Tabs */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {voiceGroups.map((group) => {
          const label = group.localeName || group.localeCode || 'Default';
          const voiceCount = group.voiceIds.filter((v) => v > 0).length;
          const personaCount = episode.personas.length;
          const isComplete = voiceCount === personaCount;
          const isActive = activeGroupId === group.id && !isCreating;

          return (
            <button
              key={group.id}
              onClick={() => {
                if (isCreating) return; // Don't switch while creating
                setActiveGroupId(group.id);
              }}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                isActive
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : isCreating
                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-default'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {label}
              <span
                className={`ml-2 text-xs ${
                  isActive ? 'text-blue-200' : isComplete ? 'text-green-600' : 'text-amber-500'
                }`}
              >
                {voiceCount}/{personaCount}
              </span>
            </button>
          );
        })}

        {/* Add button */}
        {canAddGroup && (
          <button
            onClick={handleStartCreate}
            className="px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 text-sm hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/50 transition-all"
          >
            + Add Group
          </button>
        )}
      </div>

      {/* ━━━ NEW GROUP CREATION PANEL ━━━ */}
      {isCreating && (
        <div className="mb-6 rounded-lg border-2 border-blue-200 bg-white shadow-sm overflow-hidden">
          {/* Header bar */}
          <div className="bg-blue-50 border-b border-blue-200 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              <h3 className="font-semibold text-gray-900 text-sm">New Voice Group</h3>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Locale:</label>
              <select
                value={newLocaleCode}
                onChange={(e) => setNewLocaleCode(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {!hasDefaultGroup && <option value="">Default (no locale)</option>}
                {availableLocales.map((locale) => (
                  <option key={locale.localeCode} value={locale.localeCode}>
                    {locale.name} ({locale.localeCode})
                  </option>
                ))}
              </select>
              {voices.length > 0 && (
                <button
                  type="button"
                  onClick={handleNewSuggestVoices}
                  disabled={newSuggestionsLoading}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {newSuggestionsLoading ? 'Suggesting...' : 'AI Suggest'}
                </button>
              )}
            </div>
          </div>

          {/* Voice assignments */}
          <div className="p-5">
            {voices.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-amber-800 font-medium">No voices available</p>
                <p className="text-amber-700 text-sm mt-1">
                  Add active voices for {episode.languageCode.toUpperCase()} first.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Assign a voice to each persona. All voices must be selected before creating.
                </p>
                <div className="space-y-3">
                  {episode.personas.map((persona: Persona, index: number) => {
                    const isNarrator = index === 0 && hasNarrator;
                    const selectedVoiceId = newVoiceIds[index] || '';
                    const isDuplicate = newDuplicateVoices.includes(selectedVoiceId);
                    const aiReasoning = newAiReasonings[index];
                    return (
                      <PersonaVoiceCard
                        key={index}
                        persona={persona}
                        index={index}
                        isNarrator={isNarrator}
                        narratorVoiceInfo={newGroupNarratorVoiceInfo}
                        selectedVoiceId={selectedVoiceId}
                        voices={voices}
                        isDuplicate={isDuplicate}
                        aiReasoning={aiReasoning}
                        playingVoiceId={playingVoiceId}
                        onVoiceChange={handleNewVoiceChange}
                        onTogglePlay={handleTogglePlay}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Footer actions */}
          <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-between">
            <button
              type="button"
              onClick={handleCancelCreate}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateGroup}
              disabled={
                createGroupMutation.isPending ||
                voices.length === 0 ||
                newVoiceIds.some(
                  (id, i) => !id && !(i === 0 && hasNarrator && newGroupNarratorVoiceInfo)
                )
              }
              className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {createGroupMutation.isPending ? 'Creating...' : 'Create Voice Group'}
            </button>
          </div>
        </div>
      )}

      {/* ━━━ ACTIVE GROUP PANEL (editing existing) ━━━ */}
      {activeGroup && !isCreating && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">
                {activeGroup.localeName || activeGroup.localeCode || 'Default'}
              </h2>
              {activeGroup.localeCode && (
                <span className="text-xs text-gray-400">{activeGroup.localeCode}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {voices.length > 0 && (
                <button
                  type="button"
                  onClick={handleSuggestVoices}
                  disabled={suggestionsLoading}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  data-testid="suggest-voices-button"
                >
                  {suggestionsLoading ? 'Suggesting...' : 'AI Suggest'}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDeleteGroup(activeGroup)}
                disabled={
                  deleteGroupMutation.isPending ||
                  (episode.status === 'active' && voiceGroups.length <= 1)
                }
                className="px-3 py-1.5 text-red-500 hover:bg-red-50 rounded text-sm border border-transparent hover:border-red-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={
                  episode.status === 'active' && voiceGroups.length <= 1
                    ? 'Cannot delete last group of active episode'
                    : 'Delete this voice group'
                }
              >
                Delete
              </button>
            </div>
          </div>

          <div className="p-5">
            {voices.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                <p className="text-amber-800 font-medium">No voices available</p>
                <p className="text-amber-700 text-sm mt-1">
                  Add active voices for {episode.languageCode.toUpperCase()} first.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {episode.personas.map((persona: Persona, index: number) => {
                  const isNarrator = index === 0 && hasNarrator;
                  const selectedVoiceId = voiceIds[index] || '';
                  const isDuplicate = duplicateVoices.includes(selectedVoiceId);
                  const aiReasoning = aiReasonings[index];
                  return (
                    <PersonaVoiceCard
                      key={index}
                      persona={persona}
                      index={index}
                      isNarrator={isNarrator}
                      narratorVoiceInfo={narratorVoiceInfo}
                      selectedVoiceId={selectedVoiceId}
                      voices={voices}
                      isDuplicate={isDuplicate}
                      aiReasoning={aiReasoning}
                      playingVoiceId={playingVoiceId}
                      onVoiceChange={handleVoiceChange}
                      onTogglePlay={handleTogglePlay}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Duplicate warning */}
          {duplicateVoices.length > 0 && (
            <div className="mx-5 mb-4 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-700">
              Some voices are assigned to multiple personas. This reduces variety.
            </div>
          )}

          {/* Save bar */}
          <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/episodes')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSaveGroup}
              disabled={updateGroupMutation.isPending || voices.length === 0}
              className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {updateGroupMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {voiceGroups.length === 0 && !isCreating && (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-12 text-center">
          <div className="text-gray-300 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          </div>
          <p className="text-gray-500 mb-4">No voice groups yet</p>
          <button
            onClick={handleStartCreate}
            className="px-5 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Create First Voice Group
          </button>
        </div>
      )}
    </div>
  );
}
